import * as React from 'react';
import moment from 'moment';
import i18next from 'i18next';
import { Table, Row } from '../../components/Table/Table';
import { AppState } from '../../store';
import { CustomerThunkDispatch } from '../../store/customerOverview/types';
import { actionFetchLatestKits, actionFetchKits, actionFetchKitsByStatus, actionSetTablePage, actionSetTableSearchTerm, actionFetchKitsByBarcode, actionFetchKitsByKitId, actionFetchKitsByEmail, actionFetchKitsByCustomerName } from '../../store/customerOverview/action';
import { connect } from 'react-redux';
import { KitStatusIndicator } from '../../components/KitStatusIndicator/KitStatusIndicator';
import { KitStatus, Kit, isValidKitStatus, getKitStatusByDescription, getKitProfileId, Profile } from '../../models/customerOverview';
import { getInput, byDatetime, getPhoneNumber, getHeader } from '../../helpers/helpers';
import { Page } from '../../styles/styled-components/Customer/Customer';
import KitStatusOverview from '../../components/KitStatusOverview/KitStatusOverview';
import { RouteComponentProps } from 'react-router';
import { actionSetPageTitle } from '../../store/siteConfig/action';
import CreateUserBarView from '../../components/CreateUserBar/CreateUserBarView';
import { EmailTagName } from '../../helpers/types';

interface CustomersProps extends RouteComponentProps<any> {
  fetchLatestKits: () => Promise<void>;
  fetchKits: (from: number, size: number, status?: KitStatus) => Promise<void>;
  fetchKitsByStatus: (status: KitStatus) => Promise<void>;
  fetchKitsByBarcode: (barcode: string) => Promise<void>;
  fetchKitsByKitId: (kitId: string) => Promise<void>;
  fetchKitsByEmail: (email: string) => Promise<void>;
  fetchKitsByName: (customerName: string) => Promise<void>;
  setPage: (page: number) => void;
  setSearchTerm: (newSearchTerm: string) => void;
  setPageTitle: (title: string) => void;
  customerOverview: {
    loading: boolean;
    kitsData: {
      kits: Kit[];
      total: number;
    };
    profiles: Record<string, Profile>;
    kitStatusFilter: KitStatus | undefined;
    searchTerm: string;
    currentPage: number;
  };
}

class Customers extends React.Component<CustomersProps> {

  componentDidMount() {
    this.props.setPageTitle(i18next.t('cmp.header'));
    this.props.fetchLatestKits();
  }

  render() {
    const { currentPage, kitsData, loading, profiles, searchTerm } = this.props.customerOverview;

    const HEADER: Row = getHeader(searchTerm);
    return (
      <Page title={i18next.t('cmp.header')}>

        <CreateUserBarView onLoadKits={this.onLoadKits} />

        <KitStatusOverview />
        <Table
          onRowClick={this.onRowClick}
          onPageChange={this.onPageChange}
          onSearchTermChange={this.onSearchTermChange}
          search={this.search}
          setPage={this.props.setPage}
          header={HEADER}
          data={getTableData(kitsData.kits, profiles, searchTerm)}
          loading={loading}
          rowCount={kitsData.total}
          currentPage={currentPage}
          searchTerm={searchTerm}
          searchPlaceholder={i18next.t('cmp.search.text')}
        />
      </Page>
    );
  }
  onLoadKits = () =>
    this.props.fetchLatestKits();

  onPageChange = (page: number, itemsPerPage: number) => {
    this.props.fetchKits((page - 1) * itemsPerPage, itemsPerPage, this.props.customerOverview.kitStatusFilter);
  }

  onRowClick = (row: Row) => {
    this.props.history.push(`/customer-details?code=${row.barcode}`);
  }

  onSearchTermChange = (newSearchTerm: string) =>
    this.props.setSearchTerm(newSearchTerm);

  search = (searchTerm: string) => {
    const findWhat = searchTerm.trim().toLowerCase();

    if (!findWhat) {
      this.props.fetchLatestKits();
      return;
    }

    if (findWhat === 'report released') {
      this.props.fetchKitsByStatus('ready');
      return;
    }

    if (isValidKitStatus(findWhat)) {
      if (findWhat === 'nokit') return;
      this.props.fetchKitsByStatus(findWhat);
      return;
    }

    const kitStatus = getKitStatusByDescription(findWhat);
    if (kitStatus) {
      if (findWhat === 'nokit') return;
      this.props.fetchKitsByStatus(kitStatus);
      return;
    }

    const parsed = getInput(findWhat);
    if (parsed.barcode) {
      this.props.fetchKitsByBarcode(parsed.barcode);
      return;
    }

    if (parsed.uuid) {
      this.props.fetchKitsByKitId(parsed.uuid);
      return;
    }

    if (parsed.email) {
      this.props.fetchKitsByEmail(parsed.email);
      return;
    }

    if (parsed.name) {
      this.props.fetchKitsByName(parsed.name);
      return;
    }
  };
}

function getTableData(kits: Kit[], profiles: Record<string, Profile>, searchTerm: string) {
  const kitsData = kits
    .map(kit => {
      const profileId = getKitProfileId(kit);
      const profile = profileId && profiles[profileId]
        ? profiles[profileId]
        : { name: { firstName: '', lastName: '' }, email: [], phone: [], tag: [] };
      const { tag } = profile;
      kit.history && kit.history.sort(byDatetime);
      const latestStatusDateTime = (kit.history && kit.history[0] || { datetime: '' }).datetime;
      // const latestStatusDateTime = kit.history?.find(e => e.status === 'activated' && e.datetime)?.datetime;
      const { phoneNo, countryCode } = getPhoneNumber(profile.phone);
      const customerTag = tag.find(s => s.tag.startsWith(EmailTagName.tag));
      const ownerTag = tag.find(s => s.tag.startsWith('ownershipTransferred'));
      return {
        email: (profile.email.find(e => e.name === EmailTagName.b2bcustomer && e.email) || profile.email.find(e => e.primary && e.email) || { email: '-' }).email,
        firstName: profile.name.firstName || '-',
        lastName: profile.name.lastName || '-',
        phone: (phoneNo && countryCode) ? `${countryCode}${phoneNo}` : '-',
        barcode: kit.barcode || '-',
        status: <KitStatusIndicator status={kit.status} reportReady={isReportReady(kit, searchTerm)} isReportRelesed={isReportRelesed(kit)} />,
        reportReleased: customerTag ? (ownerTag && ownerTag.tag) ? 'Yes' : 'No' : 'Yes',
        rejectedStatus: (tag.findIndex(s => s.tag.startsWith(`replaced:${kit.barcode || ''}`))) > 0 ? 'Yes' : 'No',
        timestamp: latestStatusDateTime,
      };
    })
    .sort((a, b) => moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf());
  return kitsData.map((kit) => {
    const profileInfo: Row = {
      email: kit.email,
      firstName: kit.firstName,
      lastName: kit.lastName,
      phone: kit.phone,
      barcode: kit.barcode,
      status: kit.status,
      statusDate: moment(kit.timestamp).format('DD/MM/YY'),
    };
    console.log('searchterm', searchTerm);
    switch (searchTerm) {
      case 'rejected':
        profileInfo['rejected'] = kit.rejectedStatus;
        break;
      default:
        profileInfo['reportReleased'] = kit.reportReleased;
        break;

    }
    return profileInfo;
  });
}

export function isReportReady(kit: Kit, searchTerm?: string): boolean {
  if (searchTerm)
    return kit.status === 'ready'
      && searchTerm !== 'ready'
      && kit.test !== undefined
      && kit.test.some(test => test.status === 'report-ready');
  else
    return kit.status === 'ready'
      && kit.test !== undefined
      && kit.test.some(test => test.status === 'report-ready');
}
export function isReportRelesed(kit: Kit): boolean {
  return kit.status === 'ready'
    && kit.test !== undefined
    && kit.test.some(test => test.status === 'report-result-ready');
}

const mapStateToProps = (state: AppState) => {
  return {
    customerOverview: state.customerOverview,
  };
};

const mapDispatchToProps = (dispatch: CustomerThunkDispatch) => {
  return {
    fetchLatestKits: () => dispatch(actionFetchLatestKits()),
    fetchKits: (from: number, size: number, status: KitStatus | undefined) => dispatch(actionFetchKits(from, size, status)),
    fetchKitsByStatus: (status: KitStatus) => dispatch(actionFetchKitsByStatus(status)),
    fetchKitsByBarcode: (barcode: string) => dispatch(actionFetchKitsByBarcode(barcode)),
    fetchKitsByKitId: (kitId: string) => dispatch(actionFetchKitsByKitId(kitId)),
    fetchKitsByEmail: (email: string) => dispatch(actionFetchKitsByEmail(email)),
    fetchKitsByName: (customerName: string) => dispatch(actionFetchKitsByCustomerName(customerName)),
    setPage: (page: number) => dispatch(actionSetTablePage(page)),
    setSearchTerm: (newSearchTerm: string) => dispatch(actionSetTableSearchTerm(newSearchTerm)),
    setPageTitle: (title: string) => dispatch(actionSetPageTitle({ pageTitle: title })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Customers);
